'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
  onScrollToId?: (id: string) => void;
}

export default function Slide23Ambassador({ id, registerRef, onScrollToId }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#f5f0e8]">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_190db03dc-1777290743319.png"
          alt="Bình minh tại hồ Trị An, Đồng Nai, Việt Nam"
          className="w-full h-full object-cover opacity-8"
        />
        <div className="absolute inset-0 bg-[#f5f0e8]/92" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-16 min-h-screen flex flex-col justify-center">
        {/* Header */}
        <div className="fade-in-up mb-2 text-center">
          <p className="font-display font-600 text-[clamp(11px,1.4vw,14px)] text-[#4caf7d] uppercase tracking-[0.15em] mb-2">
            Tri An Green Travel
          </p>
          <h2 className="font-display font-900 text-[clamp(22px,3.5vw,44px)] leading-[1.1] text-[#1a4a2a] tracking-tight uppercase">
            Chương trình Student Ambassador
          </h2>
          <div className="w-[60px] h-[3px] bg-gradient-to-r from-[#4caf7d] to-[#a8d87a] rounded-full mx-auto mt-4" />
        </div>

        {/* 3-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 fade-in-up mt-8">
          {/* Col 1: Mục đích */}
          <div className="rounded-2xl border border-[rgba(26,74,42,0.2)] bg-white/65 p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-[#1a4a2a] flex items-center justify-center flex-shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a8d87a" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
              </div>
              <h3 className="font-display font-700 text-[15px] text-[#1a4a2a]">Mục đích chương trình</h3>
            </div>
            <p className="font-body text-[13px] text-[#3a5a3a] leading-relaxed mb-3">
              Chương trình Student Ambassador được xây dựng nhằm tìm kiếm và đồng hành cùng những sinh viên phù hợp với tinh thần của Tri An Green Travel: tích cực, có trách nhiệm, có khả năng lan tỏa và mong muốn tham gia lâu dài vào các hoạt động cộng đồng, truyền thông và trải nghiệm.
            </p>
            <p className="font-body text-[13px] text-[#3a5a3a] leading-relaxed">
              Đây không chỉ là một vai trò đại diện hình ảnh, mà còn là cơ hội để sinh viên tiếp tục phát triển sau chương trình, đồng thời giúp Tri An Green Travel xây dựng một lực lượng đồng hành gần gũi, phù hợp với môi trường sinh viên và nhà trường.
            </p>
          </div>

          {/* Col 2: Đối tượng */}
          <div className="rounded-2xl border border-[rgba(26,74,42,0.2)] bg-white/65 p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-[#1a4a2a] flex items-center justify-center flex-shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a8d87a" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <h3 className="font-display font-700 text-[15px] text-[#1a4a2a]">Đối tượng phù hợp</h3>
            </div>
            <p className="font-body text-[13px] text-[#3a5a3a] leading-relaxed mb-3">Chương trình hướng đến những sinh viên:</p>
            <ul className="space-y-2 flex-1">
              {[
                'đã từng tham gia các hoạt động của Tri An Green Travel hoặc có sự quan tâm thực sự đến chương trình',
                'có tinh thần tích cực, trách nhiệm và thái độ nghiêm túc',
                'có hình ảnh cá nhân phù hợp, cách chia sẻ tự nhiên, tạo thiện cảm',
                'có khả năng kết nối trong trường, khoa, CLB hoặc cộng đồng sinh viên',
                'mong muốn phát triển thêm kỹ năng truyền thông, sự kiện, kết nối cộng đồng hoặc hình ảnh cá nhân',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#4caf7d] font-700 text-[13px] flex-shrink-0 mt-0.5">—</span>
                  <span className="font-body text-[12px] text-[#3a5a3a] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Vai trò */}
          <div className="rounded-2xl border border-[rgba(26,74,42,0.2)] bg-white/65 p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-[#1a4a2a] flex items-center justify-center flex-shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a8d87a" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
              <h3 className="font-display font-700 text-[15px] text-[#1a4a2a]">Vai trò của Student Ambassador</h3>
            </div>
            <p className="font-body text-[13px] text-[#3a5a3a] leading-relaxed mb-3">
              Student Ambassador sẽ là nhóm sinh viên đồng hành cùng Tri An Green Travel trong các hoạt động phù hợp, bao gồm:
            </p>
            <ul className="space-y-2 flex-1">
              {[
                'chia sẻ trải nghiệm thật sau chương trình',
                'lan tỏa thông tin đến cộng đồng sinh viên phù hợp',
                'hỗ trợ truyền thông cho các đợt hoạt động tiếp theo',
                'tham gia các sự kiện, hoạt động cộng đồng hoặc chương trình trải nghiệm khi cần',
                'góp phần xây dựng hình ảnh gần gũi, tích cực và đáng tin cậy cho thương hiệu',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#4caf7d] font-700 text-[13px] flex-shrink-0 mt-0.5">—</span>
                  <span className="font-body text-[12px] text-[#3a5a3a] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Xem thêm */}
        {onScrollToId && (
          <div className="fade-in-up text-center mt-7">
            <button
              onClick={() => onScrollToId('slide-24')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1a4a2a] text-white font-display font-600 text-[13px] hover:bg-[#2a6a3a] transition-colors"
            >
              Xem thêm — Tiêu chí & Quyền lợi
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-[rgba(26,74,42,0.15)] flex flex-col sm:flex-row items-center justify-center gap-4 fade-in-up">
          <span className="font-body text-[11px] text-[#3a5a3a]/70 uppercase tracking-wider">HOTLINE: 0827092079</span>
          <span className="hidden sm:block text-[#3a5a3a]/30">|</span>
          <span className="font-body text-[11px] text-[#3a5a3a]/70 uppercase tracking-wider">EMAIL: triangreentravel@gmail.com</span>
        </div>
      </div>
    </SlideWrapper>
  );
}
